import { openDB } from "idb";
import storage from "localforage";
import {
  AnyState,
  call,
  Err,
  Ok,
  Operation,
  PersistAdapter,
  Result,
  select as starfxSelect,
  sleep,
} from "starfx";

import { PERSISTOR_NAME } from "@app/state/constants";
import { schema } from "@app/statefx/schema";
import { processSkipForwardPersistor } from "@app/statefx/selectors/applog.sel";
import { getPxWorker } from "@app/statefx/workers/pxworker-factory";
import { allowlist, AppState } from "@app/statefx/xstore";
import { debounceThx } from "@app/statefx/xThunks/ui-persist";

type AllowedKeys = keyof AppState;
// const once = false;
export const CONST_PERSISTOR_VERSION = 9;
//
export function imperativePersist() {
  const pxWorker = getPxWorker();
  if (!pxWorker) return;
  const state = window.fx.getState();
  const allowedState = allowlist.reduce<Partial<AppState>>((acc, key) => {
    (acc as any)[key as AllowedKeys] = state[key];
    return acc;
  }, {});
  const jsonState = JSON.stringify(allowedState);
  const buffer = new TextEncoder().encode(jsonState).buffer;
  pxWorker.postMessage(
    {
      type: "/runner/arrayBuffer",
      data: buffer,
    },
    [buffer]
  );
}
// window.addEventListener("unload", imperativePersist);
// //

export function* forwardPersitence(
  key: string = null,
  jsonState: string = null
) {
  if (jsonState === null) {
    const s_ = yield* starfxSelect((a) => a);
    const s = {};
    for (const k of allowlist) {
      s[k] = s_[k];
    }
    jsonState = JSON.stringify(s);
  }

  const who = yield* starfxSelect(schema.whoami.select);

  if (who.wai_database) {
    const pxWorker = getPxWorker();
    if (!pxWorker) {
      // console.log("no pxWorker");
    }
    if (pxWorker) {
      // custom debounce middleware
      const canPost = yield* call(() => debounceThx.run());

      if (canPost.key === "out") {
        return Ok(undefined);
      }

      // includes the lock and the collection active
      const shouldSkip = yield* starfxSelect(processSkipForwardPersistor);
      console.log("shouldSkip", shouldSkip);
      // create the waiting mechanism but exit if the lock is on
      if (shouldSkip) {
        return Ok(undefined);
      }
      if (canPost.key === "in") {
        // efficient way to send the data
        // Encode the JSON string to an ArrayBuffer
        const buffer = new TextEncoder().encode(jsonState).buffer;
        console.log(buffer.byteLength, "before"); // 4MB
        pxWorker.postMessage(
          {
            type: "/runner/arrayBuffer",
            data: buffer,
          },
          [buffer]
        );
        console.log(buffer.byteLength, "after"); //0 bytes
      }
    }
  }
}

function* allowPost(): Operation<boolean> {
  const who = yield* starfxSelect(schema.whoami.select);
  if (who.wai_database) {
    const pxWorker = getPxWorker();
    if (!pxWorker) {
      console.log("no pxWorker");
    }
    if (pxWorker) {
      const canPost = yield* call(() => debounceThx.run());
      if (canPost.key === "out") {
        return false;
      }
      const shouldSkip = yield* starfxSelect(processSkipForwardPersistor);
      if (shouldSkip) {
        return false;
      }
      if (canPost.key === "in") {
        return true; // CALL SYNC //
      }
    }
  }
  return false;
}

export const openDbfn = (pName = PERSISTOR_NAME) => {
  if (!pName) throw new Error("pName is required");

  return openDB(pName, CONST_PERSISTOR_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("persist")) {
        db.createObjectStore("persist");
      }
      // ui
      if (!db.objectStoreNames.contains("ui")) {
        db.createObjectStore("ui");
      }
      // workermain
      if (!db.objectStoreNames.contains("workermain")) {
        db.createObjectStore("workermain");
      }
    },
  });
};

export function createIDBStorageAdapter<S extends AnyState>(
  pName: string
): PersistAdapter<S> {
  storage.config({
    driver: storage.INDEXEDDB,
    name: pName,
    version: CONST_PERSISTOR_VERSION,
    storeName: "persist",
  });

  return {
    getItem: function* get(key: string): Operation<Result<S>> {
      try {
        const ostorage = yield* call(storage.getItem(key)) as Operation<string>;
        const returnvalue = JSON.parse(ostorage || "{}");
        return Ok(returnvalue);
      } catch (error) {
        console.error("Error getting item from storage:", error);
        return Err(error);
      }
    },
    setItem: function* set(key: string, s: Partial<S>) {
      const state = JSON.stringify(s);
      try {
        yield* call(storage.setItem(key, state));

        // forward a slice of the state to persist in redis.
        yield* forwardPersitence(key, state);
        // maybe is too much ....
        return Ok(undefined);
      } catch (error) {
        console.error("Error setting item to storage:", error, key, state);
        return Err(error);
      }
    },
    removeItem: function* remove(key: string) {
      try {
        yield* call(storage.removeItem(key));
        return Ok(undefined);
      } catch (error) {
        console.error("Error removing item from storage:", error);
        return Err(error);
      }
    },
  };
}

// export function uiStateAdapter<S extends AnyState>(
//   pName: string
// ): PersistAdapter<S> {
//   storage.config({
//     driver: storage.INDEXEDDB,
//     name: pName,
//     version: CONST_PERSISTOR_VERSION,
//     // storeName: "ui",
//   });

//   return {
//     getItem: function* get(key: string): Operation<Result<S>> {
//       try {
//         const ostorage = yield* call(storage.getItem(key)) as Operation<string>;
//         const returnvalue = JSON.parse(ostorage || "{}");
//         return Ok(returnvalue);
//       } catch (error) {
//         console.error("Error getting item from storage:", error);
//         return Err(error);
//       }
//     },
//     setItem: function* set(key: string, s: Partial<S>) {
//       const state = JSON.stringify(s);
//       try {
//         yield* call(storage.setItem(key, state));
//         return Ok(undefined);
//       } catch (error) {
//         console.error("Error setting item to storage:", error);
//         return Err(error);
//       }
//     },
//     removeItem: function* remove(key: string) {
//       try {
//         yield* call(storage.removeItem(key));
//         return Ok(undefined);
//       } catch (error) {
//         console.error("Error removing item from storage:", error);
//         return Err(error);
//       }
//     },
//   };
// }

async function checkEstimate() {
  if (navigator.storage && navigator.storage.persist) {
    navigator.storage.persist().then((persistent) => {
      if (persistent) {
        console.log(
          "Storage will not be cleared except by explicit user action"
        );
      } else {
        console.log("Storage may be cleared by the UA under storage pressure.");
      }
    });
  }
  const estimate = await navigator.storage.estimate();
  // Math help from https://developer.mozilla.org/en-US/docs/Web/API/StorageManager/estimate
  const percentage = ((estimate.usage / estimate.quota) * 100).toFixed(2) + "%";
  const total = (estimate.quota / 1024 / 1024).toFixed(2) + "MB";
  const used = (estimate.usage / 1024 / 1024).toFixed(2) + "MB";
  console.log(`This site is using ${used} (${percentage}) of ${total}.`);
}

checkEstimate();

// export function createIDBWorkerAdapter<S extends AnyState>(
//   pName,
// ): PersistAdapter<S> {
//   const idbStorage = openDbfn(pName);
//   return {
//     getItem: function* get(key: string) {
//       const storage = yield* call(
//         (yield* call(idbStorage)).get('workermain', key),
//       );
//       return Ok(JSON.parse(storage || '{}')); //  || '{}' is added to prevent undefined that blocks the app
//     },
//     setItem: function* get(key: string, s: Partial<S>) {
//       const state = JSON.stringify(s);
//       try {
//         yield* call((yield* call(idbStorage)).put('workermain', state, key));
//       } catch (err: any) {
//         return Err(err);
//       }
//       return Ok(undefined);
//     },
//     removeItem: function* get(key: string) {
//       yield* call((yield* call(idbStorage)).delete('workermain', key));
//       return Ok(undefined);
//     },
//   };
// }

// export function uiStateAdapter<S extends AnyState>(pName) {
//   const idbStorage = openDbfn(pName);
//   return {
//     getItem: function* get(key: string) {
//       const storage = yield* call((yield* call(idbStorage)).get("ui", key));
//       return Ok(JSON.parse(storage || "{}"));
//     },
//     setItem: function* get(key: string, s: Partial<S>) {
//       const state = JSON.stringify(s);
//       try {
//         yield* call((yield* call(idbStorage)).put("ui", state, key));
//       } catch (err: any) {
//         return Err(err);
//       }
//       return Ok(undefined);
//     },
//     removeItem: function* get(key: string) {
//       yield* call((yield* call(idbStorage)).delete("ui", key));
//       return Ok(undefined);
//     },
//   };
// }
